import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Box, Breadcrumbs, Button, Link, Stack, Typography } from '@mui/material';

import { Item } from '../../styles/common';
import { CRM_PATHS } from './crmPaths';

export interface CrmCrumb {
  label: string;
  /** Omitted on the last crumb: it is the page you are on. */
  to?: string;
}

interface CrmPageFrameProps {
  title: string;
  /** One line under the title saying what the screen is for. */
  description?: string;
  /** Trail after "CRM". Defaults to the title alone. */
  crumbs?: Array<CrmCrumb>;
  /** Buttons on the right of the title row: "Log a call", "Tag selection", … */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * The frame every CRM screen renders inside: breadcrumb trail, title row with an
 * actions slot, then the body. It fetches nothing — each screen owns its queries
 * and their loading and error states, which go in the body.
 */
export const CrmPageFrame = ({ title, description, crumbs, actions, children }: CrmPageFrameProps) => {
  const trail = crumbs ?? [{ label: title }];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Item>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ m: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
              <Link component={RouterLink} to={CRM_PATHS.contacts} underline="hover" color="inherit">
                CRM
              </Link>
              {trail.map((crumb) =>
                crumb.to ? (
                  <Link key={crumb.label} component={RouterLink} to={crumb.to} underline="hover" color="inherit">
                    {crumb.label}
                  </Link>
                ) : (
                  <Typography key={crumb.label} color="text.primary" aria-current="page">
                    {crumb.label}
                  </Typography>
                ),
              )}
            </Breadcrumbs>

            <Typography variant="h4" component="h1" sx={{ wordBreak: 'break-word' }}>
              {title}
            </Typography>

            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            )}
          </Box>

          {actions && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
              {actions}
            </Box>
          )}
        </Stack>
      </Item>

      <Box sx={{ mt: 2 }}>{children}</Box>
    </Box>
  );
};

interface CrmPendingScreenProps {
  /** The board cards that build this screen's body. Omitted when no card does yet. */
  cards?: Array<number>;
  /** The plan section it comes from, for a screen with no card: "plan 4.5, wave 3". */
  plan?: string;
  /** What the API still lacks, when the screen cannot be built on today's API. */
  needsApi?: string;
  /** The API the screen's body will call, once it exists: the operations, in a sentence. */
  api?: string;
  /** What the screen will hold, one bullet each. */
  plans: Array<string>;
}

/**
 * Stands in for a screen's body until it is built. Never seen in production:
 * a screen listed in CRM_PENDING (crmPaths.ts) has no route or sidebar entry
 * there. The card that builds the body replaces this and removes that entry.
 */
export const CrmPendingScreen = ({ cards, plan, needsApi, api, plans }: CrmPendingScreenProps) => (
  <Item>
    <Alert severity="info" sx={{ m: 1 }}>
      <AlertTitle>
        {cards?.length
          ? `Built in card${cards.length > 1 ? 's' : ''} ${cards.join(' and ')}`
          : `Planned (${plan ?? 'NationBuilder migration plan, Part 4'}); no card yet`}
      </AlertTitle>
      This frame is in place so the route, the sidebar entry and the links to it work now.
      <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2.5 }}>
        {plans.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Box>
    </Alert>
    {needsApi && (
      <Alert severity="warning" sx={{ m: 1 }}>
        <AlertTitle>Needs API Integration</AlertTitle>
        {needsApi}
      </Alert>
    )}
    {api && (
      <Alert severity="success" sx={{ m: 1 }}>
        <AlertTitle>API ready (card 17.5)</AlertTitle>
        {api}
      </Alert>
    )}
  </Item>
);

interface CrmInvalidIdProps {
  /** The path segment as typed. */
  param: string | undefined;
  /** "contact", "workflow", "campaign". */
  noun: string;
  /** The list to go back to. */
  listLabel: string;
  listPath: string;
}

/**
 * A detail route whose id is not a whole number. Refused here rather than sent:
 * a malformed id is a broken link, and the API's answer to it would be a
 * validation error, not "no such record".
 */
export const CrmInvalidId = ({ param, noun, listLabel, listPath }: CrmInvalidIdProps) => (
  <CrmPageFrame
    title={`${noun.charAt(0).toUpperCase()}${noun.slice(1)} not found`}
    crumbs={[{ label: listLabel, to: listPath }, { label: 'Not found' }]}
  >
    <Alert
      severity="warning"
      action={
        <Button component={RouterLink} to={listPath} color="inherit" size="small">
          Back to {listLabel.toLowerCase()}
        </Button>
      }
    >
      <AlertTitle>"{param}" is not a {noun} id</AlertTitle>
      {`${noun.charAt(0).toUpperCase()}${noun.slice(1)} links end in a whole number, such as ${listPath}/42.`}
    </Alert>
  </CrmPageFrame>
);
